import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint, StackFrame } from "@sentry/react";

import { getRuntimeConfig } from "../config";

let initialized = false;

const SERVICE_TAG = "marker/web";
const REDACTED = "[redacted]";
// ASSUMED(no frontend warning token is sealed; audio_peaks_fallback names the
// bounded missing-peaks decode path.)
const FRONTEND_WARNING_CODES = new Set(["audio_peaks_fallback"]);

export type FrontendWarningCode = "audio_peaks_fallback";

const CAPTURE_ONLY_INTEGRATIONS = new Set([
  "InboundFilters",
  "EventFilters",
  "FunctionToString",
  "BrowserApiErrors",
  "GlobalHandlers",
  "Dedupe",
]);

type SentryException = NonNullable<
  NonNullable<ErrorEvent["exception"]>["values"]
>[number];

type SentryDebugMeta = NonNullable<ErrorEvent["debug_meta"]>;
type SentryDebugImage = NonNullable<SentryDebugMeta["images"]>[number];

/*
 * Privacy boundary for stack frames (docs/11 §3.1).
 *
 * §3.1 bans *customer data* from telemetry — transcript content, call audio,
 * caller identity, account data, credentials. It does not ban *code identity*,
 * and a browser event stripped of code identity is untriageable: the whole
 * point of the hosted source-map seam (docs/12 §3) is that Sentry resolves a
 * minified frame either by `debug_id` — joined to the uploaded artifact
 * through `debug_meta.images[].code_file` — or, failing that, by matching the
 * frame's `abs_path` / `filename` against the release's artifacts. A frame
 * carrying only `{in_app, lineno, colno}` can match neither, so an event from
 * a correctly configured project is still unreadable.
 *
 * So the allowlist keeps exactly the build-time identifiers — `function`,
 * `module`, `filename`, `abs_path`, `debug_id`, `lineno`, `colno`, `in_app`.
 * Every one of them is a constant of the compiled bundle; no runtime value
 * flows into them.
 *
 * Two things are still stripped, because they are the routes by which runtime
 * data can reach something that merely *looks* like a code identifier:
 *
 * - a URL's **query string and fragment** — cache-busting params and signed
 *   asset tokens live there, and they are never part of a bundle's identity;
 * - a URL's **scheme, userinfo, host and port** — on a self-managed connected
 *   install that host names the customer or an internal hostname. It is
 *   replaced by `~`, Sentry's own host wildcard and a documented
 *   artifact-lookup candidate, so symbolication still resolves. The same
 *   rewrite is applied to `debug_meta.images[].code_file` so the frame ↔ image
 *   join survives, and to the protocol-relative `//host/path` form. Anything
 *   that does not reduce to `~/…` or a server-relative `/…` is dropped
 *   outright — other schemes (`file:`, `blob:`, `chrome-extension:`,
 *   `webpack-internal:`) are dev-only or third-party and a `file:` path
 *   carries a developer's home directory, and a scheme-less `host/path`
 *   reference is just an authority in disguise.
 *
 * `context_line` / `pre_context` / `post_context` / `vars` stay dropped — those
 * ARE source and runtime *content* (the SDK also refuses them via
 * `frameContextLines: 0` and `stackFrameVariables: false`).
 *
 * Every kept string is additionally length-bounded and shape-checked rather
 * than passed through — symbols must be whitespace-free code tokens, locations
 * must reduce to a host-agnostic path — so a bundler config (or an engine)
 * that stuffs anything unexpected into a frame fails closed (field omitted)
 * instead of egressing.
 *
 * ASSUMED(sentry-frame-code-identity): docs/11 §3.1 bans customer data but
 * states no per-field stack-frame policy, so "build-time code identifiers are
 * not customer data, with URL authority + query stripped" is the boundary
 * drawn here.
 */
const MAX_SYMBOL_LENGTH = 120;
const MAX_CODE_LOCATION_LENGTH = 256;
/*
 * A compiled-bundle symbol is a single whitespace-free code token, or a
 * `.`/`/`-qualified chain of them: `submitTranscript`, `Object.<anonymous>`,
 * `Array.prototype.map`, `?` (Sentry's own UNKNOWN_FUNCTION), and webpack-style
 * module ids such as `./src/app.tsx`. Nothing a bundler emits here contains a
 * space, so whitespace is rejected outright — that is what keeps a sentence of
 * customer prose (which would otherwise satisfy any identifier charset) from
 * riding out in `frame.function`.
 */
const SYMBOL_TOKEN = "(?:<[A-Za-z0-9_$]+>|[A-Za-z0-9_$?-]+)";
const SAFE_SYMBOL = new RegExp(
  `^(?:\\.{1,2}/)?${SYMBOL_TOKEN}(?:[./]${SYMBOL_TOKEN})*$`,
);
/*
 * V8 prefixes constructor and async frames with a bare keyword (`new Foo`,
 * `async Foo`). The keyword is stack-format noise rather than part of the
 * symbol, so it is stripped before the no-whitespace rule is applied.
 */
const SYMBOL_FRAME_KEYWORD = /^(?:new|async) /;
const SAFE_CODE_LOCATION = /^[A-Za-z0-9_$~@.,;+%()[\]<>/-]+$/;
const URL_AUTHORITY_PREFIX = /^(?:https?:)?\/\//i;
const DEBUG_ID = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

function keepCaptureOnlyIntegrations<T extends { name: string }>(
  integrations: T[],
): T[] {
  return integrations.filter((integration) =>
    CAPTURE_ONLY_INTEGRATIONS.has(integration.name),
  );
}

/** Bound a compiled-bundle symbol (`function` / `module`), or drop it. */
function sanitizeSymbol(value: unknown): string | undefined {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > MAX_SYMBOL_LENGTH
  ) {
    return undefined;
  }
  const symbol = value.replace(SYMBOL_FRAME_KEYWORD, "");
  return SAFE_SYMBOL.test(symbol) ? symbol : undefined;
}

/**
 * Reduce a frame/artifact URL to its host-agnostic bundle path (`~/assets/…`).
 *
 * Drops the query string, the fragment, and the whole scheme/userinfo/host/port
 * authority; drops the value entirely when it is not an `http(s)` URL, a
 * protocol-relative `//host/path` URL, or a server-relative path. Anything the
 * rewrite does not reduce to `~/…` or `/…` may still carry an authority, so it
 * fails closed rather than passing through.
 */
function sanitizeCodeLocation(value: unknown): string | undefined {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > MAX_CODE_LOCATION_LENGTH
  ) {
    return undefined;
  }
  const [withoutQuery] = value.split(/[?#]/, 1);
  if (withoutQuery === undefined || withoutQuery.length === 0) {
    return undefined;
  }
  let location = withoutQuery;
  if (URL_AUTHORITY_PREFIX.test(location)) {
    // `https://host/path` and the protocol-relative `//host/path`: everything
    // between the `//` and the first path separator is authority.
    const authorityStart = location.indexOf("//") + 2;
    const pathStart = location.indexOf("/", authorityStart);
    if (pathStart === -1) {
      // Host with no path: no bundle identity worth keeping.
      return undefined;
    }
    location = `~${location.slice(pathStart)}`;
  } else if (!location.startsWith("/")) {
    // Another scheme (`file:`, `blob:`, `chrome-extension:`,
    // `webpack-internal:`) or a scheme-less `host/path` reference — neither is
    // a host-agnostic bundle path.
    return undefined;
  }
  return SAFE_CODE_LOCATION.test(location) ? location : undefined;
}

/** Keep a source-map debug id only in its canonical UUID form. */
function sanitizeDebugId(value: unknown): string | undefined {
  if (typeof value !== "string" || !DEBUG_ID.test(value)) {
    return undefined;
  }
  return value;
}

function sanitizeLineNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    return undefined;
  }
  return value;
}

function sanitizeFrame(frame: StackFrame): StackFrame {
  const sanitized: StackFrame = {};
  const filename = sanitizeCodeLocation(frame.filename);
  if (filename !== undefined) {
    sanitized.filename = filename;
  }
  const absPath = sanitizeCodeLocation(frame.abs_path);
  if (absPath !== undefined) {
    sanitized.abs_path = absPath;
  }
  const functionName = sanitizeSymbol(frame.function);
  if (functionName !== undefined) {
    sanitized.function = functionName;
  }
  const moduleName = sanitizeSymbol(frame.module);
  if (moduleName !== undefined) {
    sanitized.module = moduleName;
  }
  // Normally already hoisted into `debug_meta` by the SDK before `beforeSend`;
  // kept here so a frame that still carries one stays symbolicatable.
  const debugId = sanitizeDebugId(frame.debug_id);
  if (debugId !== undefined) {
    sanitized.debug_id = debugId;
  }
  if (typeof frame.in_app === "boolean") {
    sanitized.in_app = frame.in_app;
  }
  const lineno = sanitizeLineNumber(frame.lineno);
  if (lineno !== undefined) {
    sanitized.lineno = lineno;
  }
  const colno = sanitizeLineNumber(frame.colno);
  if (colno !== undefined) {
    sanitized.colno = colno;
  }
  return sanitized;
}

function sanitizeDebugImage(image: SentryDebugImage): SentryDebugImage[] {
  // Only source-map images are meaningful for a browser bundle; `wasm` and
  // `macho` images cannot come from this SPA, so they are dropped rather than
  // forwarded unexamined.
  if (image.type !== "sourcemap") {
    return [];
  }
  const codeFile = sanitizeCodeLocation(image.code_file);
  const debugId = sanitizeDebugId(image.debug_id);
  if (codeFile === undefined || debugId === undefined) {
    return [];
  }
  return [{ type: "sourcemap", code_file: codeFile, debug_id: debugId }];
}

function sanitizeDebugMeta(
  debugMeta: ErrorEvent["debug_meta"],
): SentryDebugMeta | undefined {
  const images = debugMeta?.images;
  if (images === undefined) {
    return undefined;
  }
  const sanitized = images.flatMap(sanitizeDebugImage);
  return sanitized.length > 0 ? { images: sanitized } : undefined;
}

function sanitizeException(exception: SentryException): SentryException {
  const sanitized: SentryException = {
    type: "Error",
    value: REDACTED,
  };
  const frames = exception.stacktrace?.frames;
  if (frames !== undefined) {
    sanitized.stacktrace = { frames: frames.map(sanitizeFrame) };
  }
  return sanitized;
}

function sanitizeEvent(
  event: ErrorEvent,
  _hint: EventHint,
  installProfile: string,
): ErrorEvent {
  const values = event.exception?.values;
  const warningMessage =
    typeof event.message === "string" &&
    FRONTEND_WARNING_CODES.has(event.message)
      ? event.message
      : undefined;
  const sanitized: ErrorEvent = {
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    message: warningMessage,
    platform: event.platform,
    // Build identity, not data; also the artifact-bundle lookup key Sentry
    // falls back to when a frame carries no `debug_id`.
    release: event.release,
    environment: event.environment,
    type: undefined,
    debug_meta: sanitizeDebugMeta(event.debug_meta),
    tags: {
      service: SERVICE_TAG,
      "marker.install_profile": installProfile,
    },
    exception: {
      values:
        values !== undefined && values.length > 0
          ? values.map(sanitizeException)
          : [{ type: "Error", value: REDACTED }],
    },
  };
  return sanitized;
}

/** Emit a bounded, PII-free warning through the configured frontend seam. */
export function captureFrontendWarning(code: FrontendWarningCode): void {
  Sentry.captureMessage(code, { level: "warning" });
}

/**
 * Initialize Sentry for `marker-web` (ADR-0003), gated on runtime config.
 *
 * The SPA ships as static assets under one image set, so the DSN, environment,
 * and release are injected at serve time on `window.__MARKER_CONFIG__.sentry`
 * — never bundled. When no DSN is injected the exporter stays OFF: air-gap
 * never injects one, and self_managed_connected is a customer opt-in. The
 * `environment` + `release` mirror the backend `MARKER_ENVIRONMENT` +
 * `MARKER_VERSION` so a staging event is distinguishable from production and
 * every event carries the real release.
 *
 * `beforeSend` rebuilds every event from an allowlist. It keeps the build-time
 * frame identity hosted symbolication needs and drops everything that can
 * carry customer data — see the stack-frame privacy boundary above.
 */
export function initSentry(): void {
  if (initialized) {
    return;
  }
  const { sentry } = getRuntimeConfig();
  if (!sentry?.dsn) {
    return;
  }
  Sentry.init({
    dsn: sentry.dsn,
    environment: sentry.environment,
    release: sentry.release,
    integrations: keepCaptureOnlyIntegrations,
    beforeBreadcrumb: () => null,
    beforeSend: (event, hint) =>
      sanitizeEvent(event, hint, sentry.installProfile),
    dataCollection: {
      cookies: false,
      frameContextLines: 0,
      genAI: {
        inputs: false,
        outputs: false,
      },
      httpBodies: [],
      httpHeaders: {
        request: false,
        response: false,
      },
      queryParams: false,
      stackFrameVariables: false,
      userInfo: false,
    },
    enableLogs: false,
    enableMetrics: false,
    enhanceFetchErrorMessages: false,
    initialScope: {
      tags: {
        service: SERVICE_TAG,
        "marker.install_profile": sentry.installProfile,
      },
    },
    maxBreadcrumbs: 0,
    normalizeDepth: 0,
    normalizeMaxBreadth: 0,
    profilesSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,
    sendDefaultPii: false,
    sendClientReports: false,
    tracePropagationTargets: [],
  });
  initialized = true;
}
